// src/components/admin/Dashboard.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Papa from "papaparse";
import { AuthContext } from "../../context/AuthContext";
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExitToApp as LogoutIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Key as KeyIcon,
  AssignmentInd as AssignIcon,
  ViewList as ViewIcon,
  MoreVert as MoreVertIcon,
  GetApp as DownloadIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import EmployeeManagement from "./EmployeeManagement";
import ApiKeyManagement from "./ApiKeyManagement";
import { FormControl, InputLabel, Select, Checkbox } from "@mui/material";

const AdminDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [selectAllJobs, setSelectAllJobs] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // CSV Import states
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);

  // For job reassignment
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [jobToReassign, setJobToReassign] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  // For action menu
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Update selectAllJobs state when all jobs are selected or deselected
    if (jobs.length > 0 && selectedJobs.length === jobs.length) {
      setSelectAllJobs(true);
    } else {
      setSelectAllJobs(false);
    }
  }, [selectedJobs, jobs]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch jobs
      const jobsRes = await axios.get("/jobs/all");
      setJobs(jobsRes.data.jobs);

      // Fetch employees
      const usersRes = await axios.get("/users/employees");
      setEmployees(usersRes.data.employees);

      setError("");
      setSelectedJobs([]); // Reset selected jobs when refreshing data
    } catch (error) {
      setError("Failed to load data. Please try again.");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleCreateJob = () => {
    navigate("/admin/job/new");
  };

  const handleEditJob = (jobId) => {
    navigate(`/admin/job/${jobId}`);
  };

  const openJobActionsMenu = (event, job) => {
    setActionMenuAnchorEl(event.currentTarget);
    setSelectedJob(job);
  };

  const closeJobActionsMenu = () => {
    setActionMenuAnchorEl(null);
    setSelectedJob(null);
  };

  const openDeleteDialog = (job) => {
    setJobToDelete(job);
    setDeleteDialogOpen(true);
    closeJobActionsMenu();
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setJobToDelete(null);
  };

  const handleDeleteJob = async () => {
    if (!jobToDelete) return;

    try {
      await axios.delete(`/jobs/${jobToDelete._id}`);
      // Refresh job list
      const updatedJobs = jobs.filter((job) => job._id !== jobToDelete._id);
      setJobs(updatedJobs);
      // Remove from selected jobs if it was selected
      setSelectedJobs(selectedJobs.filter((id) => id !== jobToDelete._id));
      closeDeleteDialog();
    } catch (error) {
      setError("Failed to delete job. Please try again.");
      console.error("Error deleting job:", error);
    }
  };

  const openReassignDialog = (job) => {
    setJobToReassign(job);
    setSelectedEmployeeId(job.assignedTo?._id || "");
    setReassignDialogOpen(true);
    closeJobActionsMenu();
  };

  const closeReassignDialog = () => {
    setReassignDialogOpen(false);
    setJobToReassign(null);
    setSelectedEmployeeId("");
  };

  const handleReassignJob = async () => {
    if (!jobToReassign) return;

    try {
      await axios.post(`/jobs/${jobToReassign._id}/reassign`, {
        employeeId: selectedEmployeeId || null,
      });

      // Update local jobs state
      const updatedJobs = jobs.map((job) => {
        if (job._id === jobToReassign._id) {
          const assignedEmployee = selectedEmployeeId
            ? employees.find((emp) => emp._id === selectedEmployeeId)
            : null;

          return {
            ...job,
            assignedTo: assignedEmployee,
          };
        }
        return job;
      });

      setJobs(updatedJobs);
      closeReassignDialog();
    } catch (error) {
      setError("Failed to reassign job. Please try again.");
      console.error("Error reassigning job:", error);
    }
  };

  const handleViewJobDetails = (jobId) => {
    navigate(`/admin/job/${jobId}/view`);
    closeJobActionsMenu();
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getStatusChip = (status) => {
    let color = "default";

    switch (status) {
      case "pending":
        color = "warning";
        break;
      case "in-progress":
        color = "info";
        break;
      case "completed":
        color = "success";
        break;
      default:
        color = "default";
    }

    return (
      <Chip
        label={status.toUpperCase()}
        color={color}
        size="small"
        sx={{ textTransform: "capitalize" }}
      />
    );
  };

  // Handle job selection for CSV export
  const handleJobSelect = (jobId) => {
    const isSelected = selectedJobs.includes(jobId);

    if (isSelected) {
      setSelectedJobs(selectedJobs.filter((id) => id !== jobId));
    } else {
      setSelectedJobs([...selectedJobs, jobId]);
    }
  };

  // Handle select all jobs
  const handleSelectAllJobs = () => {
    if (selectAllJobs) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(jobs.map((job) => job._id));
    }
    setSelectAllJobs(!selectAllJobs);
  };

  // Open export dialog
  const openExportDialog = () => {
    if (selectedJobs.length === 0) {
      setError("Please select at least one job to export");
      return;
    }
    setExportDialogOpen(true);
  };

  // Close export dialog
  const closeExportDialog = () => {
    setExportDialogOpen(false);
  };

  // Export jobs to CSV
  const exportJobsToCsv = async () => {
    if (selectedJobs.length === 0) {
      setError("No jobs selected for export");
      return;
    }

    try {
      setExportLoading(true);
      const response = await axios.post(
        "/jobs/export",
        {
          jobIds: selectedJobs,
        },
        {
          responseType: "blob",
        },
      );

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `jobs_export_${new Date().toISOString().split("T")[0]}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      closeExportDialog();
      setSelectedJobs([]); // Reset selected jobs after successful export
      setError("");
    } catch (error) {
      setError(
        "Failed to export jobs data: " +
          (error.response?.data?.message || error.message),
      );
      console.error("Error exporting CSV:", error);
    } finally {
      setExportLoading(false);
    }
  };

  // Open CSV import dialog
  const openCsvDialog = () => {
    setCsvDialogOpen(true);
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setCsvErrors([]);
    setImportResults(null);
  };

  // Close CSV import dialog
  const closeCsvDialog = () => {
    setCsvDialogOpen(false);
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setCsvErrors([]);
    setImportResults(null);
  };

  // Handle CSV file selection
  const handleCsvFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCsvFile(file);
    setCsvErrors([]);
    setCsvData([]);
    setCsvHeaders([]);
    setImportResults(null);

    // Parse CSV file
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsedData = results.data || [];
        const parsedHeaders = results.meta?.fields || [];

        console.log("CSV Parse Results:");
        console.log("Detected headers:", parsedHeaders);
        console.log("First row of data:", parsedData[0]);

        // Normalize headers to lowercase for validation
        const normalizedHeaders = parsedHeaders.map((h) =>
          h.toLowerCase().trim(),
        );

        setCsvHeaders(parsedHeaders);
        setCsvData(parsedData);

        // Validate required columns (case-insensitive)
        const requiredColumns = [
          "job_number",
          "customer_name",
          "technician_name",
          "location_address",
        ];
        const missingColumns = requiredColumns.filter(
          (col) => !normalizedHeaders.includes(col),
        );

        if (missingColumns.length > 0) {
          console.log("Missing columns:", missingColumns);
          console.log("Required columns:", requiredColumns);
          setCsvErrors([
            `Missing required columns: ${missingColumns.join(", ")}`,
          ]);
        } else {
          console.log("All required columns found!");
        }
      },
      error: (error) => {
        console.error("Papa Parse error:", error);
        setCsvErrors([error.message || "Failed to parse CSV file"]);
      },
    });
  };

  // Import CSV data
  const handleImportCsv = async () => {
    if (csvData.length === 0) {
      setError("No data to import");
      return;
    }

    if (csvErrors.length > 0) {
      setError("Please fix CSV errors before importing");
      return;
    }

    try {
      setImportLoading(true);
      const response = await axios.post("/jobs/bulk-import", {
        jobs: csvData,
      });

      setImportResults(response.data.results);
      setError("");

      // Refresh jobs list if any were successful
      if (response.data.results.successful.length > 0) {
        await fetchData();
      }
    } catch (error) {
      setError(
        "Failed to import jobs: " +
          (error.response?.data?.message || error.message),
      );
      console.error("Error importing CSV:", error);
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            HVAC Service App - Admin
          </Typography>
          {user && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body1" sx={{ mr: 2 }}>
                Hi, {user.name}
              </Typography>
              <IconButton color="inherit" onClick={handleLogout}>
                <LogoutIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab icon={<WorkIcon />} label="Jobs" />
              <Tab icon={<PersonIcon />} label="Technicians" />
              <Tab icon={<KeyIcon />} label="API Keys" />
            </Tabs>

            <Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchData}
                sx={{ mr: 2 }}
              >
                Refresh
              </Button>

              {tabValue === 0 && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={openCsvDialog}
                    sx={{ mr: 2 }}
                  >
                    Import CSV
                  </Button>
                  {selectedJobs.length > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={openExportDialog}
                      sx={{ mr: 2 }}
                    >
                      Export Selected ({selectedJobs.length})
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateJob}
                  >
                    New Job
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TabPanel value={tabValue} index={0}>
                {jobs.length === 0 ? (
                  <Alert severity="info">
                    No jobs found. Create your first job to get started.
                  </Alert>
                ) : (
                  <TableContainer
                    component={Paper}
                    sx={{ maxHeight: "calc(100vh - 230px)", overflowY: "auto" }}
                  >
                    <Table stickyHeader aria-label="jobs table">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectAllJobs}
                              onChange={handleSelectAllJobs}
                              indeterminate={
                                selectedJobs.length > 0 &&
                                selectedJobs.length < jobs.length
                              }
                            />
                          </TableCell>
                          <TableCell>Job Number</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Assigned To</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobs.map((job) => (
                          <TableRow
                            key={job._id}
                            hover
                            selected={selectedJobs.includes(job._id)}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedJobs.includes(job._id)}
                                onChange={() => handleJobSelect(job._id)}
                              />
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {job.name}
                            </TableCell>
                            <TableCell>{formatDate(job.date)}</TableCell>
                            <TableCell>
                              {job.assignedTo?.name || "Unassigned"}
                            </TableCell>
                            <TableCell>{getStatusChip(job.status)}</TableCell>
                            <TableCell align="center">
                              <IconButton
                                aria-label="job actions"
                                onClick={(e) => openJobActionsMenu(e, job)}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <EmployeeManagement />
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <ApiKeyManagement />
              </TabPanel>
            </>
          )}
        </Paper>
      </Container>

      {/* Job Actions Menu */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={closeJobActionsMenu}
      >
        <MenuItem onClick={() => handleViewJobDetails(selectedJob?._id)}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleEditJob(selectedJob?._id)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Job</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => openReassignDialog(selectedJob)}>
          <ListItemIcon>
            <AssignIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reassign</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => openDeleteDialog(selectedJob)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the job "{jobToDelete?.name}"? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteJob} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reassign Job Dialog */}
      <Dialog open={reassignDialogOpen} onClose={closeReassignDialog}>
        <DialogTitle>Reassign Job</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select an employee to assign this job to:
          </DialogContentText>

          <FormControl fullWidth>
            <InputLabel>Employee</InputLabel>
            <Select
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              label="Employee"
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {employees.map((employee) => (
                <MenuItem key={employee._id} value={employee._id}>
                  {employee.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReassignDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleReassignJob}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={closeExportDialog}>
        <DialogTitle>Export Jobs</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to export {selectedJobs.length}{" "}
            {selectedJobs.length === 1 ? "job" : "jobs"} to CSV. This will
            include all job details and equipment data for the selected jobs.
          </DialogContentText>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Note: For jobs with no equipment, only the job details will be
            included in the CSV.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeExportDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={exportJobsToCsv}
            color="primary"
            variant="contained"
            disabled={exportLoading}
            startIcon={
              exportLoading ? <CircularProgress size={20} /> : <DownloadIcon />
            }
          >
            {exportLoading ? "Exporting..." : "Export"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog
        open={csvDialogOpen}
        onClose={closeCsvDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Jobs from CSV</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Upload a CSV file with the following columns: job_number,
            customer_name, technician_name, location_address, date (optional),
            status (optional), description (optional)
          </DialogContentText>

          <Box sx={{ mb: 2 }}>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleCsvFileChange}
              style={{ display: "none" }}
              id="csv-upload-input"
            />
            <label htmlFor="csv-upload-input">
              <Button variant="outlined" component="span" fullWidth>
                {csvFile ? csvFile.name : "Select CSV File"}
              </Button>
            </label>
          </Box>

          {csvErrors.length > 0 && (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                {csvErrors.map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please make sure your CSV file includes these required columns:
                job_number, customer_name, technician_name, location_address
              </Typography>
            </Box>
          )}

          {csvData.length > 0 && csvErrors.length === 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preview ({csvData.length} rows found):
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {csvHeaders.map((header) => (
                        <TableCell key={header}>{header}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvData.slice(0, 10).map((row, idx) => (
                      <TableRow key={idx}>
                        {csvHeaders.map((header) => (
                          <TableCell key={header}>{row[header]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {csvData.length > 10 && (
                <Typography variant="caption" color="text.secondary">
                  Showing first 10 of {csvData.length} rows
                </Typography>
              )}
            </Box>
          )}

          {importResults && (
            <Box sx={{ mt: 2 }}>
              <Alert
                severity={
                  importResults.failed.length === 0 ? "success" : "warning"
                }
                sx={{ mb: 2 }}
              >
                Import completed: {importResults.successful.length} successful,{" "}
                {importResults.failed.length} failed
              </Alert>

              {importResults.failed.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                    Failed Rows:
                  </Typography>
                  <TableContainer
                    component={Paper}
                    sx={{ maxHeight: 200, mb: 2 }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Job Number</TableCell>
                          <TableCell>Errors</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importResults.failed.map((fail, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{fail.row}</TableCell>
                            <TableCell>
                              {fail.data?.job_number || "N/A"}
                            </TableCell>
                            <TableCell>
                              {fail.errors.map((err, i) => (
                                <div key={i}>{err}</div>
                              ))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCsvDialog} color="primary">
            Close
          </Button>
          {csvData.length > 0 && csvErrors.length === 0 && !importResults && (
            <Button
              onClick={handleImportCsv}
              color="primary"
              variant="contained"
              disabled={importLoading}
              startIcon={
                importLoading ? <CircularProgress size={20} /> : <UploadIcon />
              }
            >
              {importLoading ? "Importing..." : "Import Jobs"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// TabPanel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default AdminDashboard;
